import { useCallback, useId, useRef, useState, type FormEvent, type MutableRefObject } from 'react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Select, SelectOption } from '@/components/ui/select';
import { api } from '@/lib/api';
import { useDialogFocusRestore } from '@/lib/hooks/useDialogFocusRestore';
import {
  createReceiverRequest,
  receiverFormError,
  receiverScopeLabel,
  receiverToForm,
  toggleEventType,
  updateReceiverRequest,
  webhookErrorMessage,
  EMPTY_RECEIVER_FORM,
  EVENT_TYPES,
  type ReceiverFormError,
  type ReceiverFormField,
  type WebhookReceiverForm,
} from '@/lib/webhooks';
import type { APIKey } from '@/types/apiKey';
import type { WebhookReceiver, WebhookReceiverWithSecret } from '@/types/webhook';

/** null keeps the dialog closed. */
export type ReceiverDialogTarget = { mode: 'create' } | { mode: 'edit'; receiver: WebhookReceiver } | null;

interface WebhookReceiverDialogProps {
  target: ReceiverDialogTarget;
  /** undefined while loading or after a failed load. */
  apiKeys: APIKey[] | undefined;
  apiKeysError: string | null;
  onClose: () => void;
  /** Create succeeded; the parent closes this dialog and reveals the secret. */
  onCreated: (created: WebhookReceiverWithSecret) => void;
  /** Edit succeeded. */
  onSaved: () => void;
}

/** Shell always mounted; the form body mounts only while a target is set, so state resets on every open. */
export function WebhookReceiverDialog({ target, apiKeys, apiKeysError, onClose, onCreated, onSaved }: WebhookReceiverDialogProps) {
  const submittingRef = useRef(false);
  const { captureOpener, restoreFocus } = useDialogFocusRestore();
  return (
    <Dialog
      open={target !== null}
      onOpenChange={(next) => {
        if (!next && !submittingRef.current) onClose();
      }}
    >
      {target ? (
        <FormBody
          key={target.mode === 'edit' ? `edit:${target.receiver.id}` : 'create'}
          target={target}
          apiKeys={apiKeys}
          apiKeysError={apiKeysError}
          onClose={onClose}
          onCreated={onCreated}
          onSaved={onSaved}
          submittingRef={submittingRef}
          captureOpener={captureOpener}
          restoreFocus={restoreFocus}
        />
      ) : null}
    </Dialog>
  );
}

interface FormBodyProps extends Omit<WebhookReceiverDialogProps, 'target'> {
  target: NonNullable<ReceiverDialogTarget>;
  submittingRef: MutableRefObject<boolean>;
  captureOpener: () => void;
  restoreFocus: (event: Event) => void;
}

const checkboxClass = 'size-4 accent-primary outline-none focus-visible:ring-2 focus-visible:ring-ring';

function FormBody({ target, apiKeys, apiKeysError, onClose, onCreated, onSaved, submittingRef, captureOpener, restoreFocus }: FormBodyProps) {
  const editing = target.mode === 'edit' ? target.receiver : null;
  const ids = useId();
  const cancelRef = useRef<HTMLButtonElement>(null);
  const [form, setForm] = useState<WebhookReceiverForm>(() => (editing ? receiverToForm(editing) : EMPTY_RECEIVER_FORM));
  const [submitting, setSubmitting] = useState(false);
  // Server errors: one alert above the footer. Client-side checks: a message under the offending field.
  const [error, setError] = useState<string | null>(null);
  const [fieldError, setFieldError] = useState<ReceiverFormError | null>(null);
  const nameRef = useRef<HTMLInputElement>(null);
  const urlRef = useRef<HTMLInputElement>(null);
  const keyRef = useRef<HTMLSelectElement>(null);
  const apiKeyScopeRef = useRef<HTMLInputElement>(null);

  const alertRef = useCallback((el: HTMLParagraphElement | null) => {
    el?.focus();
  }, []);

  /** Applies a form change and clears the field error once the user edits that field. */
  const change = (patch: Partial<WebhookReceiverForm>, field?: ReceiverFormField) => {
    setForm({ ...form, ...patch });
    if (fieldError && fieldError.field === field) setFieldError(null);
  };

  /** aria props tying a field to its client-side error message. */
  const invalidProps = (field: ReceiverFormField) =>
    fieldError?.field === field ? { 'aria-invalid': true, 'aria-describedby': `${ids}-${field}-error` } : {};

  const fieldMessage = (field: ReceiverFormField) =>
    fieldError?.field === field ? (
      <p id={`${ids}-${field}-error`} className="text-xs text-destructive">
        {fieldError.message}
      </p>
    ) : null;

  const activeKeys = (apiKeys ?? []).filter((k) => k.is_active);
  // Without a select to point at, the API key radio carries the "select a key" error.
  const keySelectShown = apiKeys !== undefined && activeKeys.length > 0;
  const keyNames = new Map((apiKeys ?? []).map((k) => [k.id, k.name]));

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (submittingRef.current) return;
    const invalid = receiverFormError(form, editing ? 'edit' : 'create');
    if (invalid) {
      setError(null);
      setFieldError(invalid);
      const target =
        invalid.field === 'name' ? nameRef.current : invalid.field === 'url' ? urlRef.current : (keyRef.current ?? apiKeyScopeRef.current);
      target?.focus();
      return;
    }
    setFieldError(null);
    if (editing) {
      const changes = updateReceiverRequest(editing, form);
      if (Object.keys(changes).length === 0) {
        onClose();
        return;
      }
      submittingRef.current = true;
      setSubmitting(true);
      setError(null);
      try {
        await api.webhooks.update(editing.id, changes);
        toast.success('Receiver saved');
        onSaved();
      } catch (err) {
        setError(webhookErrorMessage(err, 'Failed to save receiver'));
        setSubmitting(false);
      } finally {
        submittingRef.current = false;
      }
      return;
    }
    submittingRef.current = true;
    setSubmitting(true);
    setError(null);
    try {
      const created = await api.webhooks.create(createReceiverRequest(form));
      onCreated(created); // parent closes this dialog and opens the one-time reveal
    } catch (err) {
      setError(webhookErrorMessage(err, 'Failed to create receiver'));
      setSubmitting(false);
    } finally {
      submittingRef.current = false;
    }
  };

  const blockWhileSubmitting = (e: Event) => {
    if (submittingRef.current) e.preventDefault();
  };

  return (
    <DialogContent
      className="max-h-[90vh] overflow-y-auto sm:max-w-lg"
      onOpenAutoFocus={(e) => {
        captureOpener();
        e.preventDefault();
        cancelRef.current?.focus();
      }}
      onCloseAutoFocus={restoreFocus}
      onEscapeKeyDown={blockWhileSubmitting}
      onPointerDownOutside={blockWhileSubmitting}
      onInteractOutside={blockWhileSubmitting}
    >
      <DialogHeader>
        <DialogTitle>{editing ? 'Edit receiver' : 'Add receiver'}</DialogTitle>
        <DialogDescription>
          {editing
            ? 'Scope cannot be changed. Inactive receivers get no deliveries.'
            : 'The bridge POSTs signed app events to this URL. The signing secret is shown once after creation.'}
        </DialogDescription>
      </DialogHeader>
      <form onSubmit={(e) => void submit(e)} className="space-y-4" noValidate>
        <div className="space-y-2">
          <label htmlFor={`${ids}-name`} className="text-sm font-medium">
            Name
          </label>
          <Input
            ref={nameRef}
            id={`${ids}-name`}
            value={form.name}
            onChange={(e) => change({ name: e.target.value }, 'name')}
            {...invalidProps('name')}
            required
            maxLength={255}
            placeholder="SaaS production"
          />
          {fieldMessage('name')}
        </div>

        <div className="space-y-2">
          <label htmlFor={`${ids}-url`} className="text-sm font-medium">
            URL
          </label>
          <Input
            ref={urlRef}
            id={`${ids}-url`}
            type="url"
            inputMode="url"
            value={form.url}
            onChange={(e) => change({ url: e.target.value }, 'url')}
            {...invalidProps('url')}
            required
            maxLength={2048}
            spellCheck={false}
            placeholder="https://saas.example.com/hooks/bridge"
            className="font-mono text-sm"
          />
          {fieldMessage('url')}
          <p className="text-xs text-muted-foreground">Must be https. Private, loopback and link-local addresses are rejected.</p>
        </div>

        {editing ? (
          <div className="space-y-1 text-sm">
            <div className="font-medium">Scope</div>
            <div className="text-muted-foreground">{receiverScopeLabel(editing.api_key_id, keyNames)}</div>
          </div>
        ) : (
          <fieldset className="space-y-2">
            <legend className="text-sm font-medium">Scope</legend>
            <label className="flex cursor-pointer items-center gap-2 text-sm select-none">
              <input
                type="radio"
                name={`${ids}-scope`}
                value="global"
                checked={form.scope === 'global'}
                onChange={() => change({ scope: 'global' }, 'apiKeyId')}
                className={checkboxClass}
              />
              Global (all apps)
            </label>
            <label className="flex cursor-pointer items-center gap-2 text-sm select-none">
              <input
                ref={apiKeyScopeRef}
                type="radio"
                name={`${ids}-scope`}
                value="api_key"
                checked={form.scope === 'api_key'}
                onChange={() => change({ scope: 'api_key' })}
                {...(keySelectShown ? {} : invalidProps('apiKeyId'))}
                className={checkboxClass}
              />
              API key (only apps created with that key)
            </label>
            {form.scope === 'api_key' ? (
              <div className="space-y-1 pl-6">
                <label htmlFor={`${ids}-key`} className="sr-only">
                  API key
                </label>
                {apiKeys === undefined ? (
                  <p className="text-sm text-muted-foreground">{apiKeysError ? `Could not load API keys: ${apiKeysError}` : 'Loading API keys…'}</p>
                ) : activeKeys.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No active API keys. Create one on the API Keys page first.</p>
                ) : (
                  <Select
                    ref={keyRef}
                    id={`${ids}-key`}
                    value={form.apiKeyId}
                    onValueChange={(apiKeyId) => change({ apiKeyId }, 'apiKeyId')}
                    {...invalidProps('apiKeyId')}
                  >
                    <SelectOption value="">Select an API key…</SelectOption>
                    {activeKeys.map((k) => (
                      <SelectOption key={k.id} value={String(k.id)}>
                        {k.name}
                      </SelectOption>
                    ))}
                  </Select>
                )}
                {fieldMessage('apiKeyId')}
              </div>
            ) : null}
          </fieldset>
        )}

        <fieldset className="space-y-2" aria-describedby={`${ids}-events-hint`}>
          <legend className="text-sm font-medium">Events</legend>
          <p id={`${ids}-events-hint`} className="text-xs text-muted-foreground">
            Leave all unchecked to receive every event.
          </p>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {EVENT_TYPES.map(({ type, label }) => (
              <label key={type} className="flex cursor-pointer items-start gap-2 text-sm select-none">
                <input
                  type="checkbox"
                  checked={form.eventTypes.includes(type)}
                  onChange={(e) => change({ eventTypes: toggleEventType(form.eventTypes, type, e.target.checked) })}
                  className={`mt-0.5 ${checkboxClass}`}
                />
                <span>
                  {label}
                  <span className="block font-mono text-xs text-muted-foreground">{type}</span>
                </span>
              </label>
            ))}
          </div>
        </fieldset>

        {editing ? (
          <label className="flex cursor-pointer items-center gap-2 select-none">
            <input
              type="checkbox"
              role="switch"
              checked={form.isActive}
              onChange={(e) => change({ isActive: e.target.checked })}
              className={checkboxClass}
            />
            <span className="text-sm font-medium">Active</span>
          </label>
        ) : null}

        {error ? (
          <p ref={alertRef} tabIndex={-1} role="alert" className="rounded-lg bg-destructive/10 p-3 text-sm text-destructive outline-none [overflow-wrap:anywhere]">
            {error}
          </p>
        ) : null}

        <DialogFooter className="gap-2 sm:gap-0">
          <Button ref={cancelRef} type="button" variant="outline" aria-disabled={submitting} onClick={() => !submitting && onClose()}>
            Cancel
          </Button>
          <Button type="submit" aria-disabled={submitting} aria-busy={submitting}>
            {submitting ? <Loader2 className="animate-spin" aria-hidden="true" /> : null}
            {submitting ? 'Saving…' : editing ? 'Save' : 'Add receiver'}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}
